// Form Template Manager
// Centralized management of all form templates in the application

import getFieldAssessmentFormTemplate from './FieldAssessmentForm';
import getIncidentReportFormTemplate from './IncidentReportForm';
import getTrainingRecordFormTemplate from './TrainingRecordForm';
import getDailyFarmLogbookFormTemplate from './DailyFarmLogbookForm';

class FormTemplateManager {
  constructor() {
    this.templates = new Map();
    this.initialized = false;
  }

  // Initialize all form templates
  initialize() {
    if (this.initialized) return;

    // Register all form templates
    this.registerTemplate('field-assessment', getFieldAssessmentFormTemplate);
    this.registerTemplate('incident-report', getIncidentReportFormTemplate);
    this.registerTemplate('training-record', getTrainingRecordFormTemplate);
    this.registerTemplate('daily-farm-logbook', getDailyFarmLogbookFormTemplate);

    this.initialized = true;
  }

  // Register a new form template
  registerTemplate(type, templateFunction) {
    if (typeof templateFunction !== 'function') {
      throw new Error(`Template for ${type} must be a function`);
    }
    this.templates.set(type, templateFunction);
  }

  // Get a specific form template by type
  getTemplate(type, t) {
    if (!this.initialized) {
      this.initialize();
    }

    const templateFunction = this.templates.get(type);
    if (!templateFunction) {
      throw new Error(`Form template for type '${type}' not found`);
    }

    try {
      return templateFunction(t);
    } catch (error) {
      console.error(`Error generating template for ${type}:`, error);
      throw new Error(`Failed to generate template for ${type}: ${error.message}`);
    }
  }

  // Get all available form template types
  getAvailableTypes() {
    if (!this.initialized) {
      this.initialize();
    }

    return Array.from(this.templates.keys());
  }

  // Get template metadata (title, description) for all templates
  getAllTemplateMetadata(t) {
    if (!this.initialized) {
      this.initialize();
    }

    const metadata = [];
    
    for (const [type, templateFunction] of this.templates.entries()) {
      try {
        const template = templateFunction(t);
        metadata.push({
          type,
          title: template.title,
          description: template.description,
          formType: template.formType
        });
      } catch (error) {
        console.error(`Error getting metadata for template ${type}:`, error);
      }
    }

    return metadata;
  }

  // Get localized template title
  getLocalizedTitle(template, language) {
    if (typeof template.title === 'string') {
      return template.title;
    }
    
    if (template.title && template.title[language]) {
      return template.title[language];
    }
    
    if (template.title && template.title.en) {
      return template.title.en;
    }
    
    return template.formType || 'Untitled Form';
  }

  // Get localized template description
  getLocalizedDescription(template, language) {
    if (typeof template.description === 'string') {
      return template.description;
    }
    
    if (template.description && template.description[language]) {
      return template.description[language];
    }
    
    if (template.description && template.description.en) {
      return template.description.en;
    }
    
    return '';
  }

  // Get localized field label
  getLocalizedFieldLabel(field, language) {
    if (typeof field.label === 'string') {
      return field.label;
    }
    
    if (field.label && field.label[language]) {
      return field.label[language];
    }
    
    if (field.label && field.label.en) {
      return field.label.en;
    }
    
    return field.name || 'Unnamed Field';
  }

  // Get localized field placeholder
  getLocalizedFieldPlaceholder(field, language) {
    if (!field.placeholder) return '';

    if (typeof field.placeholder === 'string') {
      return field.placeholder;
    }
    
    if (field.placeholder[language]) {
      return field.placeholder[language];
    }
    
    if (field.placeholder.en) {
      return field.placeholder.en;
    }
    
    return '';
  }

  // Get localized field options
  getLocalizedFieldOptions(options, language) {
    if (!options || !Array.isArray(options)) return options;

    return options.map(option => {
      if (typeof option === 'string') {
        return option;
      }

      if (option.label) {
        if (typeof option.label === 'string') {
          return {
            ...option,
            label: option.label
          };
        } else if (option.label[language]) {
          return {
            ...option,
            label: option.label[language]
          };
        } else if (option.label.en) {
          return {
            ...option,
            label: option.label.en
          };
        }
      }

      return option;
    });
  }
}

// Create singleton instance
const formTemplateManager = new FormTemplateManager();

export default formTemplateManager;